import { Check } from "lucide-react";
import { useEffect, useRef } from "react";

import {
  describeWatchRecipeLeaf,
  getAuthorableWatchRecipes,
  type WatchRecipeCatalog,
  type WatchRecipeDefinition,
} from "../../lib/watchRecipeCatalog";
import type { WatchSignalCatalog } from "../../lib/watchSignalCatalog";
import styles from "./WatchSignalPicker.module.css";

export interface SelectedWatchRecipeIdentity {
  readonly recipeId: string;
  readonly definitionVersion: number;
}

export interface GuidedWatchRecipesProps {
  /** Runtime-decoded server catalog. Missing and empty catalogs stay invisible. */
  recipeCatalog: WatchRecipeCatalog | null;
  /** Runtime-decoded signal authority used to verify every immutable leaf. */
  signalCatalog: WatchSignalCatalog | null;
  selected: SelectedWatchRecipeIdentity | null;
  onSelect: (recipe: WatchRecipeDefinition | null) => void;
  /** Optional style extension applied to the root element. */
  className?: string;
}

/**
 * Offer immutable guided recipes only after both the recipe and all of its
 * leaves pass their independent reviewed server-authority boundaries.
 */
export default function GuidedWatchRecipes({
  recipeCatalog,
  signalCatalog,
  selected,
  onSelect,
  className,
}: GuidedWatchRecipesProps) {
  const recipeButtonsRef = useRef(new Map<string, HTMLButtonElement>());
  const restoreFocusToRef = useRef<string | null>(null);
  const recipes = getAuthorableWatchRecipes(recipeCatalog, signalCatalog);

  useEffect(() => {
    if (selected !== null || restoreFocusToRef.current === null) return;
    recipeButtonsRef.current.get(restoreFocusToRef.current)?.focus();
    restoreFocusToRef.current = null;
  }, [selected]);

  if (recipes.length === 0) return null;

  const selectedRecipe = recipes.find((recipe) => (
    recipe.recipe_id === selected?.recipeId
    && recipe.definition_version === selected.definitionVersion
  )) ?? null;

  return (
    <div className={`${styles.signalList} ${className ?? ""}`} aria-label="Guided recipes">
      {recipes.map((recipe) => {
        const isSelected = recipe === selectedRecipe;
        const identity = `${recipe.recipe_id}:${recipe.definition_version}`;
        const conditions = recipe.leaves.flatMap((leaf) => {
          const sentence = describeWatchRecipeLeaf(recipe, leaf);
          return sentence ? [{ leafId: leaf.leaf_id, sentence }] : [];
        });
        return (
          <div className={styles.signalGroup} key={identity}>
            <button
              ref={(node) => {
                if (node) recipeButtonsRef.current.set(identity, node);
                else recipeButtonsRef.current.delete(identity);
              }}
              type="button"
              className={isSelected ? styles.signalSelected : styles.signal}
              aria-pressed={isSelected}
              onClick={() => onSelect(isSelected ? null : recipe)}
            >
              <span className={styles.radio} aria-hidden="true">
                {isSelected ? <span /> : null}
              </span>
              <span className={styles.signalCopy}>
                <strong>{recipe.customer.name}</strong>
                <span>{recipe.customer.description}</span>
              </span>
            </button>
            {isSelected ? (
              <div className={styles.recipeConditions}>
                {recipe.recipe_id === "market.options_volatility_liquidity_trend" ? (
                  <p className={styles.recipeConditionsNote}>Use this when options should be active and reasonably easy to trade, with price trend confirmation. Implied volatility describes option pricing; it is not a recommendation.</p>
                ) : null}
                <strong className={styles.recipeConditionsHeading}>All of these must be true</strong>
                <ul className={styles.recipeConditionsList} aria-label={`${recipe.customer.name} conditions`}>
                  {conditions.map((condition) => (
                    <li key={condition.leafId}>
                      <Check aria-hidden="true" size={15} />
                      <span>{condition.sentence}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        );
      })}
      {selectedRecipe ? (
        <button type="button" className={styles.filter} onClick={() => {
          restoreFocusToRef.current = `${selectedRecipe.recipe_id}:${selectedRecipe.definition_version}`;
          onSelect(null);
        }}>
          Choose an individual signal instead
        </button>
      ) : null}
    </div>
  );
}
